import type { Theme, Direction, CommonColors, ThemeProviderProps } from '@mui/material/styles';
import type { ThemeCssVariables } from './types';
import type { PaletteColorKey, PaletteColorNoChannels } from './core/palette';

// ----------------------------------------------------------------------

export type ThemeConfig = {
  direction: Direction;
  classesPrefix: string;
  cssVariables: ThemeCssVariables;
  defaultMode: ThemeProviderProps<Theme>['defaultMode'];
  modeStorageKey: ThemeProviderProps<Theme>['modeStorageKey'];
  fontFamily: Record<'primary' | 'secondary', string>;
  palette: Record<PaletteColorKey, PaletteColorNoChannels> & {
    common: Pick<CommonColors, 'black' | 'white'>;
    grey: {
      [K in 25 | 50 | 100 | 200 | 300 | 400 | 500 | 600 | 700 | 800 | 900 as `${K}`]: string;
    };
  };
};

export const themeConfig: ThemeConfig = {
  /** **************************************
   * Base
   *************************************** */
  defaultMode: 'light',
  modeStorageKey: 'theme-mode',
  direction: 'ltr',
  classesPrefix: 'minimal',
  /** **************************************
   * Css variables
   *************************************** */
  cssVariables: {
    cssVarPrefix: '',
    colorSchemeSelector: 'data-color-scheme',
  },
  /** **************************************
   * Typography
   *************************************** */
  fontFamily: {
    primary: 'Inter',
    secondary: 'Inter',
  },
  /** **************************************
   * Palette
   *************************************** */
  palette: {
    primary: {
      lighter: '#EFF8FF',
      light: '#B2DDFF',
      main: '#2055FF',
      dark: '#194185',
      darker: '#102A55',
      contrastText: '#FFFFFF',
    },
    success: {
      lighter: '#ECFDF3',
      light: '#A6F4C5',
      main: '#12B76A',
      dark: '#027A48',
      darker: '#027A48',
      contrastText: '#FFFFFF',
    },
    warning: {
      lighter: '#FFFAEB',
      light: '#FEDF89',
      main: '#F79009',
      dark: '#B54708',
      darker: '#7A2E0E',
      contrastText: '#FFFFFF',
    },
    error: {
      lighter: '#FEF3F2',
      light: '#FECDCA',
      main: '#F04438',
      dark: '#B71D18',
      darker: '#7A271A',
      contrastText: '#FFFFFF',
    },
    grey: {
      25: '#FCFCFD',
      50: '#FCFDFD',
      100: '#F2F4F7',
      200: '#EAECF0',
      300: '#D0D5DD',
      400: '#98A2B3',
      500: '#667085',
      600: '#475467',
      700: '#344054',
      800: '#1D2939',
      900: '#101828',
    },
    common: {
      black: '#000000',
      white: '#FFFFFF',
    },
  },
};
